import { findCarbonTonsPerMwh } from "@aclymatepackages/calcs/utilities";
import { convertOfficeElectricCarbonIntensityToTonsPerMwh } from "@aclymatepackages/calcs/helpers";

// Location-based electricity for CLOSED accounting years.
//
// A closed year's report and dashboard read the frozen accounting snapshot
// (v3-companies/{id}/accounting/{docId}) instead of live data.
// monthlySubcategoryEmissionsTons only stores subcategory -> tons, and an
// electricity transaction's stored tonsCo2e is already net of any RECs
// (Market-based). So a locked year had nothing to derive Location-based from,
// and the Scope 2 page showed Location equal to Market (Fairware 2024: 9,381
// kWh fully covered by RECs read "Location 0 / Market 0", while the live
// facility electricity table on the same report showed Location 0.14 t).
//
// These parallel per-month arrays (12 numbers each, same month indexing as
// monthlySubcategoryEmissionsTons) carry what the Scope 2 page needs. They
// are deliberately NOT keys inside monthlySubcategoryEmissionsTons: every
// reader of that field (aclymate-packages' sumAccountingYearTons, baseline
// percentages, badges) sums every key it finds, so a location figure stored
// there would be counted as additional emissions.
//
// Invariant at write time: location = market ("electricity" key) + RECs tons,
// the same formula Report.js's calculateElectricityLocationAndMarketTons uses
// for an open year -- or null for a month whose RECs could not be priced,
// which the reader surfaces as "Location not available".
const toNumber = (value) => {
  if (value === undefined || value === null || value === "") {
    return 0;
  }
  const parsed =
    typeof value === "number" ? value : Number(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const hasValue = (value) => value !== undefined && value !== null && value !== "";

// Same precedence as Report.js's extractEmissionProperty: a nested
// emissionData/details copy wins over a top-level field. A row created by a
// CSV/bulk import writes these fields at the top level; a later per-
// transaction edit only ever writes emissionData, leaving the top-level
// copy stale next to a corrected nested one (confirmed live for
// electricKwh: functions/controllers/transactions.js's CSV path vs.
// helpers/components/inputs.js's per-transaction save). Preferring the
// stale top-level copy here would disagree with Report.js reading the same
// row -- the exact class of bug fixed for gasUnit fields in
// transactionContributionReducer.js's sibling gas-unit-casing fix.
const readEmissionValue = (emission, property, alternateProperty) => {
  const { emissionData = {}, details = {} } = emission || {};
  const merged = { ...emission, ...emissionData, ...details };
  return merged?.[property] ?? merged?.[alternateProperty];
};

const readEmissionNumber = (emission, property, alternateProperty) =>
  toNumber(readEmissionValue(emission, property, alternateProperty));

const sumProperty = (emissions, property, alternateProperty) =>
  emissions.reduce(
    (sum, emission) =>
      sum + readEmissionNumber(emission, property, alternateProperty),
    0
  );

// "utilities" is a combined gas+electric row; its kWh, RECs kWh and RECs
// tons are all electricity. "upstream-leased-assets" is a leased office's
// default utility usage -- a real Scope 3 category, but its kWh/RECs are
// still electricity Report.js's facility table already counts (it reads kWh
// via electricKwh, falling back to totalKwh, the field leased-office rows
// actually store it under). The Scope 2 page treats all three the same way
// (Report.js expands combined rows into electricity records that keep their
// renewableCarbonTonsCo2e), so the snapshot does too.
const isElectricitySource = ({ subcategory }) =>
  subcategory === "electricity" ||
  subcategory === "utilities" ||
  subcategory === "upstream-leased-assets";

const toDate = (value) => (value?.toDate ? value.toDate() : value);

// A row's custom carbon intensity, in tons/MWh, or undefined when none is
// set. convertOfficeElectricCarbonIntensityToTonsPerMwh treats a falsy
// input as "not set" (returns undefined), which would misread a genuine,
// intentional intensity of exactly 0 (a company asserting a real
// zero-carbon custom source) as "no custom intensity" and fall through to a
// nonzero grid-based price -- so 0 is special-cased here before delegating
// to it.
const findCustomIntensityTonsPerMwh = (row) => {
  const customIntensity = readEmissionValue(row, "electricCarbonIntensity");
  if (!hasValue(customIntensity)) {
    return undefined;
  }
  if (toNumber(customIntensity) === 0) {
    return 0;
  }
  return toNumber(
    convertOfficeElectricCarbonIntensityToTonsPerMwh(
      customIntensity,
      readEmissionValue(row, "electricCarbonIntensityUnits")
    )
  );
};

// RECs tons for one raw usage row: what the input form stored
// (renewableCarbonTonsCo2e) -- trusted whenever present, including an
// explicit 0, since that's the number the transaction's own detail view
// already shows; recomputing behind it would disagree with that view, the
// same "two views disagree" bug class this file exists to avoid. Otherwise,
// the row's own custom carbon intensity if it has one, else recsKwh x the
// row's own grid factor -- the same recomputation the backfill script does.
// Returns null when the row has RECs kWh but neither stored tons, a custom
// intensity, nor a grid to price them against: the caller then marks the
// month's Location as unknown rather than writing Location equal to Market
// next to a positive RECs kWh figure.
const findRowRecsTons = (row, findTonsPerMwh) => {
  const recsKwh = readEmissionNumber(row, "recsKwh");
  const storedRecsTons = readEmissionValue(row, "renewableCarbonTonsCo2e");

  if (hasValue(storedRecsTons)) {
    return toNumber(storedRecsTons);
  }
  if (!recsKwh) {
    return 0;
  }

  const customIntensityTonsPerMwh = findCustomIntensityTonsPerMwh(row);
  if (customIntensityTonsPerMwh !== undefined) {
    return (recsKwh / 1000) * customIntensityTonsPerMwh;
  }

  const eGrid =
    readEmissionValue(row, "eGrid") ||
    row?.office?.eGrid ||
    row?.office?.address?.eGrid;
  if (!eGrid) {
    return null;
  }

  const gridTonsPerMwh = toNumber(
    findTonsPerMwh(eGrid, toDate(row.emissionDate || row.date) || new Date())
  );
  return (recsKwh / 1000) * gridTonsPerMwh;
};

// One month of the snapshot.
// - marketEmissions: that month's allEmissions rows. Only Market comes from
//   here -- it is exactly what the "electricity" key of
//   monthlySubcategoryEmissionsTons sums, so the locked totals never change.
//   RECs tons deliberately do NOT come from here: allEmissions is built from
//   the legacy all-aggregated-transactions cache whenever the rollups-v2
//   read is off (always in admin view, where staff routinely close customer
//   years, and for every company not on the canary), and legacy aggregates
//   never carry renewableCarbonTonsCo2e.
// - usageSources: that month's raw utility rows (utilitiesTransactions plus
//   recurring and office-default utility emissions, the same sources the
//   report's "Electricity Use and Emissions By Facility" table reads). kWh,
//   RECs kWh and RECs tons all come from here, so the three always agree.
//
// locationTons is null when some row's RECs could not be priced (see
// findRowRecsTons); the reader then reports that month's Location as not
// available.
export const buildElectricityMonthSnapshot = ({
  marketEmissions = [],
  usageSources = [],
  findTonsPerMwh = findCarbonTonsPerMwh
}) => {
  const marketTons = sumProperty(
    marketEmissions.filter(({ subcategory }) => subcategory === "electricity"),
    "tonsCo2e"
  );

  const electricityUsageRows = usageSources.filter(isElectricitySource);
  const rowsRecsTons = electricityUsageRows.map((row) =>
    findRowRecsTons(row, findTonsPerMwh)
  );
  const hasUnpricedRecs = rowsRecsTons.some((tons) => tons === null);
  const recsTons = rowsRecsTons.reduce((sum, tons) => sum + (tons || 0), 0);

  return {
    locationTons: hasUnpricedRecs ? null : marketTons + recsTons,
    recsTons,
    kwh: sumProperty(electricityUsageRows, "electricKwh", "totalKwh"),
    recsKwh: sumProperty(electricityUsageRows, "recsKwh")
  };
};

export const buildElectricitySnapshotFields = (monthSnapshots = []) => ({
  monthlyElectricityLocationTons: monthSnapshots.map(
    ({ locationTons }) => locationTons
  ),
  monthlyElectricityRecsTons: monthSnapshots.map(({ recsTons }) => recsTons),
  monthlyElectricityKwh: monthSnapshots.map(({ kwh }) => kwh),
  monthlyElectricityRecsKwh: monthSnapshots.map(({ recsKwh }) => recsKwh)
});

// A snapshot written before these fields existed has no way to tell
// "Location equals Market" from "Location unknown" -- see
// convertAccountingYearsToEmissions.
export const hasElectricityLocationSnapshot = (accountingYear = {}) =>
  Array.isArray(accountingYear.monthlyElectricityLocationTons) &&
  Array.isArray(accountingYear.monthlyElectricityRecsTons);

// Per-month electricity fields to attach to the snapshot's electricity entry.
// Returns null when the snapshot predates these fields, or when that month's
// Location was recorded as unknown (null) at close.
export const readElectricityMonthSnapshot = (accountingYear, monthIndex) => {
  if (!hasElectricityLocationSnapshot(accountingYear)) {
    return null;
  }

  const {
    monthlyElectricityLocationTons,
    monthlyElectricityRecsTons,
    monthlyElectricityKwh = [],
    monthlyElectricityRecsKwh = []
  } = accountingYear;

  // An out-of-range index reads as undefined, not null -- treated the same
  // as an explicit null (both mean "unknown"), so a short/partial array
  // (a partial backfill run, or any future writer that doesn't write all 12
  // months) can't silently read back as a real 0 instead of "not available".
  const locationTons = monthlyElectricityLocationTons[monthIndex];
  if (!hasValue(locationTons)) {
    return null;
  }

  return {
    locationTons: toNumber(locationTons),
    recsTons: toNumber(monthlyElectricityRecsTons[monthIndex]),
    kwh: toNumber(monthlyElectricityKwh[monthIndex]),
    recsKwh: toNumber(monthlyElectricityRecsKwh[monthIndex])
  };
};
